import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Scales, Trophy, Plus, X, ArrowRight } from "@phosphor-icons/react";

function eur(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (Number.isNaN(v)) return String(n);
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
}

function scoreCls(score) {
    if (score == null) return "text-gray-400";
    if (score <= 40) return "text-red-600";
    if (score <= 60) return "text-orange-600";
    if (score <= 80) return "text-yellow-700";
    return "text-emerald-600";
}

export default function DealComparator() {
    const [auctions, setAuctions] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/auctions");
            setAuctions(data.filter((a) => a.status === "completed"));
        })();
    }, []);

    const toggle = (id) => {
        setSelectedIds((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 4) return prev;
            return [...prev, id];
        });
    };

    const compare = async () => {
        if (selectedIds.length < 2) return;
        setLoading(true);
        try {
            const { data } = await api.post("/comparator/compare", { auction_ids: selectedIds });
            setResult(data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="mb-8" data-testid="comparator-header">
                <div className="overline mb-2">Deal Comparator</div>
                <h1 className="font-display-black text-4xl sm:text-5xl leading-none">
                    Confronta opportunità.
                </h1>
                <p className="text-sm text-gray-600 mt-3 max-w-xl">
                    Seleziona da 2 a 4 analisi completate e ottieni una tabella di confronto
                    side-by-side con ranking e raccomandazione.
                </p>
            </div>

            {/* SELECTOR */}
            <div className="bg-white border border-gray-200 p-6 mb-6" data-testid="auction-selector">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline">Aste disponibili ({auctions.length} completate)</div>
                    <div className="text-xs text-gray-600">
                        Selezionate: <b className="font-mono-tight">{selectedIds.length}/4</b>
                    </div>
                </div>
                {auctions.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6">
                        Nessuna asta completata. <Link to="/new" className="font-semibold underline">Crea una nuova analisi</Link>.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                        {auctions.map((a) => {
                            const sel = selectedIds.includes(a.id);
                            return (
                                <button
                                    key={a.id}
                                    data-testid={`select-auction-${a.id}`}
                                    onClick={() => toggle(a.id)}
                                    className={`text-left border p-3 transition-colors ${
                                        sel ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-400"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm truncate">{a.title}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {a.province} · {eur(a.base_price)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`font-mono-tight font-bold ${scoreCls(a.investment_score)}`}>
                                                {a.investment_score ?? "—"}
                                            </span>
                                            {sel && <X size={14} weight="bold" />}
                                            {!sel && <Plus size={14} />}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="mt-5 flex justify-end gap-3">
                    <button
                        onClick={() => { setSelectedIds([]); setResult(null); }}
                        data-testid="clear-selection"
                        className="h-10 px-4 border border-gray-300 text-xs font-bold uppercase tracking-wider"
                    >
                        Reset
                    </button>
                    <button
                        onClick={compare}
                        disabled={selectedIds.length < 2 || loading}
                        data-testid="compare-button"
                        className="h-10 px-5 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
                    >
                        <Scales size={14} weight="bold" />
                        {loading ? "Confronto in corso…" : `Confronta (${selectedIds.length})`}
                    </button>
                </div>
            </div>

            {/* RESULT */}
            {result && (
                <ComparisonResult result={result} />
            )}
        </Layout>
    );
}

function ComparisonResult({ result }) {
    const { items, winner } = result;
    const winnerItem = items.find((i) => i.auction_id === winner);

    const rows = [
        ["Provincia", "province"], ["Comune", "comune"],
        ["Prezzo base", "base_price", eur],
        ["Investment Score", "investment_score"],
        ["Score band", "score_band", (v) => (v || "—").replace(/_/g, " ")],
        ["Market value", "market_value", eur],
        ["After Repair Value", "after_repair_value", eur],
        ["Canone mensile", "rental_value", eur],
        ["Offerta max consigliata", "max_recommended_bid", eur],
        ["ROI realistico", "realistic_roi_pct", (v) => v != null ? `${v}%` : "—"],
        ["Margine netto realistico", "realistic_net_margin", eur],
        ["Legal Risk Score", "legal_risk_score"],
        ["Rischi legali", "legal_risks_count"],
        ["Urban Risk Score", "urban_risk_score"],
        ["Issues urbanistici", "urban_issues_count"],
        ["Raccomandazione", "decision_recommendation", (v) => (v || "—").replace(/_/g, " ")],
    ];

    return (
        <div data-testid="comparison-result">
            {/* WINNER BANNER */}
            {winnerItem && (
                <div className="bg-black text-white p-6 mb-6 flex items-center gap-4" data-testid="winner-banner">
                    <Trophy size={32} weight="bold" className="text-yellow-400" />
                    <div className="flex-1">
                        <div className="overline text-white/60 mb-1">Ranking AI</div>
                        <div className="font-display-black text-2xl">{winnerItem.title}</div>
                        <div className="text-xs text-white/70 mt-1">
                            Investment Score più alto del confronto:
                            <b className="font-mono-tight ml-1 text-yellow-400">{winnerItem.investment_score}/100</b>
                        </div>
                    </div>
                    <Link
                        to={`/auctions/${winnerItem.auction_id}`}
                        data-testid="winner-link"
                        className="bg-white text-black px-4 h-10 text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-gray-200"
                    >
                        Apri <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            )}

            {/* COMPARISON TABLE */}
            <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="comparison-table">
                <table className="w-full text-sm" style={{ minWidth: 600 + items.length * 200 }}>
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-gray-500 w-56">Metrica</th>
                            {items.map((it) => (
                                <th key={it.auction_id} className="text-left p-3 border-l border-gray-200">
                                    <div className="font-bold text-sm truncate">{it.title}</div>
                                    <div className="text-xs text-gray-500 font-mono-tight">{it.province}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(([label, key, fmt]) => {
                            const values = items.map((it) => it[key]);
                            const numericValues = values.filter((v) => typeof v === "number");
                            const maxVal = numericValues.length > 0 ? Math.max(...numericValues) : null;
                            return (
                                <tr key={key} className="border-b border-gray-100 last:border-0">
                                    <td className="p-3 text-xs uppercase tracking-wider text-gray-500 font-semibold bg-white/[0.03]">{label}</td>
                                    {items.map((it, i) => {
                                        const v = it[key];
                                        const highlight = key === "investment_score" && v != null && v === maxVal;
                                        return (
                                            <td
                                                key={i}
                                                className={`p-3 border-l border-gray-200 font-mono-tight text-sm ${
                                                    highlight ? "bg-emerald-50 font-bold" : ""
                                                }`}
                                            >
                                                {fmt ? fmt(v) : (v != null ? String(v) : "—")}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
